import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

import type { ColTypeDef, ValueFormatterFunc } from 'ag-grid-community';

import {
  tooltipRenderer,
  totalsValueFormatter,
  type SparklineTooltipParams,
} from 'components/AgGrid/Dashboard/VendorExpenses/helpers';

export type ColumnTypeKey = 'string' | 'totals' | 'trend';

type ColumnTypes = Record<ColumnTypeKey, ColTypeDef>;

type Options = { isPriorityErp: boolean };

const useAgGridColumnTypes = ({ isPriorityErp }: Options): ColumnTypes => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return useMemo<ColumnTypes>(
    () => ({
      string: {},
      totals: {
        cellRenderer: 'amountCellRenderer',
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        },
        valueFormatter: totalsValueFormatter as unknown as ValueFormatterFunc,
      },
      trend: {
        cellRenderer: 'agSparklineCellRenderer',
        cellStyle: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        cellRendererParams: {
          sparklineOptions: {
            type: 'bar',
            direction: 'vertical',
            height: 60,
            fill: theme.palette.primary.main,
            xKey: 'x',
            yKey: 'y',
            highlight: {
              highlightedItem: {
                fill: theme.palette.primary.light,
                stroke: theme.palette.common.black,
                strokeWidth: 1,
              },
            },

            axis: {
              type: 'category',
              strokeWidth: 0,
              paddingInner: 0.1,
              paddingOuter: 0,
            },

            padding: {
              top: 2,
              right: 0,
              bottom: 0,
              left: 0,
            },

            tooltip: {
              renderer: (params: SparklineTooltipParams) =>
                tooltipRenderer(params, { formatMessage, isPriorityErp }),
            },
          },
        },
      },
    }),
    [theme, formatMessage, isPriorityErp],
  );
};

export default useAgGridColumnTypes;
